import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  MoodleService,
  CourseStructure,
  CourseSection,
  CourseContentItem,
} from '../../../services/moodle.service';

@Component({
  selector: 'app-course-structure-dialog',
  templateUrl: './course-structure-dialog.component.html',
  styleUrl: './course-structure-dialog.component.scss',
})
export class CourseStructureDialogComponent implements OnInit {
  @Input() courseId!: string;
  @Input() courseName!: string;

  structure: CourseStructure | null = null;
  loading = true;
  expandedSections: Set<string> = new Set();

  constructor(
    protected dialogRef: NbDialogRef<CourseStructureDialogComponent>,
    protected moodleService: MoodleService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.moodleService.getCourseStructure(this.courseId).subscribe({
      next: (data) => {
        this.structure = data;
        this.loading = false;
        // Expand all sections by default
        if (data?.sections) {
          data.sections.forEach((s) => this.expandedSections.add(s.id));
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  toggleSection(section: CourseSection) {
    if (this.expandedSections.has(section.id)) {
      this.expandedSections.delete(section.id);
    } else {
      this.expandedSections.add(section.id);
    }
  }

  isSectionExpanded(section: CourseSection): boolean {
    return this.expandedSections.has(section.id);
  }

  getVisibleItems(section: CourseSection): CourseContentItem[] {
    return section.items.filter((item) => !this.isHiddenTechnicalItem(item));
  }

  private isHiddenTechnicalItem(item: CourseContentItem): boolean {
    // Package parents are hidden because their extracted children carry the
    // content — except when extraction failed: then the package row is the
    // only place to tell the user that this activity was not imported.
    return (
      (item.module_type === 'scorm_package' || item.module_type === 'h5p_activity') &&
      !this.isFailedPackage(item)
    );
  }

  private isFailedPackage(item: CourseContentItem): boolean {
    return (
      (item.module_type === 'scorm_package' || item.module_type === 'h5p_activity') &&
      !!item.extraction_status &&
      item.extraction_status !== 'ok'
    );
  }

  getModuleIcon(moduleType: string): string {
    switch (moduleType) {
      case 'page':
        return 'file-text-outline';
      case 'resource':
        return 'file-outline';
      case 'book':
        return 'book-open-outline';
      case 'url':
        return 'link-outline';
      case 'label':
        return 'pricetags-outline';
      case 'lesson_page':
        return 'list-outline';
      case 'lesson_pdf':
        return 'file-text-outline';
      case 'data_entry':
        return 'grid-outline';
      case 'forum_discussion':
        return 'message-circle-outline';
      case 'quiz_content':
        return 'question-mark-circle-outline';
      case 'scorm_package':
        return 'archive-outline';
      case 'scorm_chapter':
        return 'book-outline';
      case 'scorm_slide':
        return 'monitor-outline';
      case 'scorm_sco':
        return 'cube-outline';
      case 'h5p_activity':
        return 'layers-outline';
      case 'h5p_slide':
        return 'monitor-outline';
      case 'h5p_book_page':
        return 'book-open-outline';
      case 'h5p_question':
        return 'question-mark-circle-outline';
      case 'h5p_block':
        return 'square-outline';
      case 'data_field_file':
      case 'folder_file':
      case 'page_attachment':
      case 'label_attachment':
      case 'book_attachment':
        return 'attach-2-outline';
      default:
        return 'file-outline';
    }
  }

  getModuleLabel(moduleType: string): string {
    const key = this.getModuleLabelKey(moduleType);
    if (!key) {
      return moduleType;
    }
    const translated = this.translate.instant(key);
    return translated === key ? moduleType : translated;
  }

  getModuleLabelKey(moduleType: string): string | null {
    switch (moduleType) {
      case 'page':
        return 'dialogs.courseStructure.moduleTypes.page';
      case 'resource':
        return 'dialogs.courseStructure.moduleTypes.resource';
      case 'book':
        return 'dialogs.courseStructure.moduleTypes.book';
      case 'url':
        return 'dialogs.courseStructure.moduleTypes.url';
      case 'label':
        return 'dialogs.courseStructure.moduleTypes.label';
      case 'lesson_page':
        return 'dialogs.courseStructure.moduleTypes.lessonPage';
      case 'lesson_pdf':
        return 'dialogs.courseStructure.moduleTypes.lessonPdf';
      case 'data_entry':
        return 'dialogs.courseStructure.moduleTypes.dataEntry';
      case 'quiz_content':
        return 'dialogs.courseStructure.moduleTypes.quizContent';
      case 'forum_discussion':
        return 'dialogs.courseStructure.moduleTypes.forumDiscussion';
      case 'data_field_file':
        return 'dialogs.courseStructure.moduleTypes.databaseFile';
      case 'folder_file':
        return 'dialogs.courseStructure.moduleTypes.folderFile';
      case 'page_attachment':
      case 'label_attachment':
      case 'book_attachment':
        return 'dialogs.courseStructure.moduleTypes.attachment';
      case 'scorm_package':
        return 'dialogs.courseStructure.moduleTypes.scormPackage';
      case 'scorm_chapter':
        return 'dialogs.courseStructure.moduleTypes.scormChapter';
      case 'scorm_slide':
        return 'dialogs.courseStructure.moduleTypes.scormSlide';
      case 'scorm_sco':
        return 'dialogs.courseStructure.moduleTypes.scormSco';
      case 'h5p_activity':
        return 'dialogs.courseStructure.moduleTypes.h5pActivity';
      case 'h5p_slide':
        return 'dialogs.courseStructure.moduleTypes.h5pSlide';
      case 'h5p_book_page':
        return 'dialogs.courseStructure.moduleTypes.h5pBookPage';
      case 'h5p_question':
        return 'dialogs.courseStructure.moduleTypes.h5pQuestion';
      case 'h5p_block':
        return 'dialogs.courseStructure.moduleTypes.h5pBlock';
      default:
        return null;
    }
  }

  /** Die Einbettung ist fehlgeschlagen, nicht bloss noch nicht fertig. Der
   *  Backend-Endpunkt liefert den Fehlertext nur, solange nichts eingebettet
   *  ist — beides sah in der Anzeige vorher gleich aus. */
  isFailedEmbedding(item: CourseContentItem): boolean {
    return !item.status_embedding && !!item.embedding_error;
  }

  getStatusIcon(item: CourseContentItem): string {
    if (this.isFailedPackage(item) || this.isFailedEmbedding(item)) {
      return 'alert-triangle-outline';
    }
    if (item.status_embedding) {
      return 'checkmark-circle-2-outline';
    }
    if (this.isReferenceOnlyItem(item)) {
      return 'close-circle-outline';
    }
    return 'clock-outline';
  }

  getStatusColor(item: CourseContentItem): string {
    if (this.isFailedPackage(item) || this.isFailedEmbedding(item)) {
      return 'danger';
    }
    if (item.status_embedding) {
      return 'success';
    }
    if (this.isReferenceOnlyItem(item)) {
      return 'danger';
    }
    return 'warning';
  }

  getStatusTooltip(item: CourseContentItem): string {
    if (this.isFailedPackage(item)) {
      const key =
        item.extraction_status === 'unsupported_dynamic_package'
          ? 'dialogs.courseStructure.status.packageDynamicUnsupported'
          : 'dialogs.courseStructure.status.packageNotExtracted';
      return this.translate.instant(key);
    }
    if (item.status_embedding) {
      return this.translate.instant('dialogs.courseStructure.status.embedded');
    }
    if (this.isFailedEmbedding(item)) {
      return this.translate.instant('dialogs.courseStructure.status.failed', {
        reason: item.embedding_error,
      });
    }
    if (this.isReferenceOnlyItem(item)) {
      return this.translate.instant('dialogs.courseStructure.status.referenceOnly');
    }
    return this.translate.instant('dialogs.courseStructure.status.pending');
  }

  private isReferenceOnlyItem(item: CourseContentItem): boolean {
    return item.module_type === 'url' && !item.has_file && !item.has_content;
  }

  close() {
    this.dialogRef.close();
  }
}
